import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Plus, Trash2, Save, ArrowLeft, GripVertical, X, Search, Image, Film, ChevronUp, ChevronDown, ChevronsUp, ChevronsDown, Check } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';


// Predefined colors for zones
const ZONE_COLORS = [
  '#F4A460', // Sandy brown
  '#20B2AA', // Light sea green
  '#DEB887', // Burlywood
  '#F0E68C', // Khaki
  '#87CEEB', // Sky blue
  '#DDA0DD', // Plum
  '#98FB98', // Pale green
  '#FFB6C1', // Light pink
  '#B0C4DE', // Light steel blue
  '#FFDAB9', // Peach puff
];

function LayoutEditor() {
  const navigate = useNavigate();
  const { layoutId } = useParams();
  const { currentUser, isAdmin } = useAuth();
  
  const [layoutName, setLayoutName] = useState('');
  const [layoutContentType, setLayoutContentType] = useState(''); // 'interval' | 'exercise' — gates what content zones accept
  const [isTemplate, setIsTemplate] = useState(false); // admin-only: offer this layout as a preset in the template picker
  const [orientation, setOrientation] = useState('landscape');
  const [zones, setZones] = useState([]);
  const [selectedZoneIndex, setSelectedZoneIndex] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [assets, setAssets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [signedUrlMap, setSignedUrlMap] = useState({});

  // Overlay state
  const [overlayUrl, setOverlayUrl] = useState('');
  const [uploadingOverlay, setUploadingOverlay] = useState(false);

  // Asset picker modal state
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [assetPickerZoneIndex, setAssetPickerZoneIndex] = useState(null);
  const [assetSearchQuery, setAssetSearchQuery] = useState('');

  // Drag state for zone reordering in list
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Canvas drag/resize state
  const canvasRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialZone, setInitialZone] = useState(null);

  // Keyboard arrow key handler for nudging zones
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedZoneIndex === null) return;
      
      // Don't intercept if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }
      
      // Check if it's an arrow key
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        return;
      }
      
      // Prevent page scroll
      e.preventDefault();
      
      const nudgeAmount = e.shiftKey ? 5 : 1; // Hold shift for bigger nudge
      
      setZones(prev => {
        const updated = [...prev];
        const zone = { ...updated[selectedZoneIndex] };
        
        switch (e.key) {
          case 'ArrowUp':
            zone.top = Math.max(0, parseFloat(zone.top) - nudgeAmount);
            zone.top = Math.round(zone.top * 10) / 10;
            break;
          case 'ArrowDown':
            zone.top = Math.min(100 - parseFloat(zone.height), parseFloat(zone.top) + nudgeAmount);
            zone.top = Math.round(zone.top * 10) / 10;
            break;
          case 'ArrowLeft':
            zone.left = Math.max(0, parseFloat(zone.left) - nudgeAmount);
            zone.left = Math.round(zone.left * 10) / 10;
            break;
          case 'ArrowRight':
            zone.left = Math.min(100 - parseFloat(zone.width), parseFloat(zone.left) + nudgeAmount);
            zone.left = Math.round(zone.left * 10) / 10;
            break;
          default:
            break;
        }
        
        updated[selectedZoneIndex] = zone;
        return updated;
      });
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedZoneIndex]);

  // Disable body scroll when layout editor is mounted
  useEffect(() => {
    const originalStyle = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalStyle;
    };
 }, []);

  const fetchData = useCallback(async () => {
    try {
      const [playlistsSnapshot, assetsSnapshot] = await Promise.all([
        getDocs(collection(db, 'playlists')),
        getDocs(collection(db, 'assets'))
      ]);
      const playlistsData = playlistsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const assetsData = assetsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(asset => asset.type !== 'layout');
      setPlaylists(playlistsData);
      setAssets(assetsData);
      if (layoutId) {
        const layoutDoc = assetsSnapshot.docs.find(d => d.id === layoutId);
        if (layoutDoc && layoutDoc.data().type === 'layout') {
          const layoutData = layoutDoc.data();
          setLayoutName(layoutData.name || '');
          setLayoutContentType(layoutData.layoutContentType || '');
          setIsTemplate(layoutData.isTemplate || false);
          setOrientation(layoutData.orientation || 'landscape');
          setOverlayUrl(layoutData.overlayUrl || '');
          const zonesWithDefaults = (layoutData.zones || []).map((zone, index) => ({
            ...zone,
            color: zone.color || ZONE_COLORS[index % ZONE_COLORS.length],
            fitMode: zone.fitMode || 'fit',
            zIndex: zone.zIndex !== undefined ? zone.zIndex : index
          }));
          setZones(zonesWithDefaults);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [layoutId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch signed URLs for image assets
useEffect(() => {
  const fetchSignedUrls = async () => {
    const imageAssets = assets.filter(a => a.type === 'image');
    if (imageAssets.length === 0) return;

    const keys = imageAssets.map(a => 
      a.key || a.url?.replace('https://pulse-signage.s3.us-east-2.amazonaws.com/', '')
    ).filter(Boolean);

    if (keys.length === 0) return;

    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const getSignedUrls = httpsCallable(functions, 'getSignedUrls');
      
      const result = await getSignedUrls({ keys });
      if (result.data?.signedUrls) {
        setSignedUrlMap(result.data.signedUrls);
      }
    } catch (error) {
      console.error('Failed to fetch signed URLs:', error);
    }
  };

  fetchSignedUrls();
}, [assets]);

  // Mouse move handler for drag/resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!canvasRef.current || (!isDragging && !isResizing)) return;
      
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const canvasWidth = rect.width;
      const canvasHeight = rect.height;
      
      // Calculate mouse position as percentage
      const mouseXPercent = ((e.clientX - rect.left) / canvasWidth) * 100;
      const mouseYPercent = ((e.clientY - rect.top) / canvasHeight) * 100;
      
      // Calculate delta from drag start
      const deltaX = mouseXPercent - dragStart.x;
      const deltaY = mouseYPercent - dragStart.y;
      
      if (isDragging && selectedZoneIndex !== null && initialZone) {
        // Move zone
        let newLeft = initialZone.left + deltaX;
        let newTop = initialZone.top + deltaY;
        
        // Clamp to canvas bounds
        newLeft = Math.max(0, Math.min(100 - initialZone.width, newLeft));
        newTop = Math.max(0, Math.min(100 - initialZone.height, newTop));
        
        setZones(prev => {
          const updated = [...prev];
          updated[selectedZoneIndex] = {
            ...updated[selectedZoneIndex],
            left: Math.round(newLeft * 10) / 10,
            top: Math.round(newTop * 10) / 10
          };
          return updated;
        });
      } else if (isResizing && selectedZoneIndex !== null && initialZone && resizeHandle) {
        // Resize zone based on handle
        let newLeft = initialZone.left;
        let newTop = initialZone.top;
        let newWidth = initialZone.width;
        let newHeight = initialZone.height;
        
        // Handle different resize handles
        if (resizeHandle.includes('w')) {
          // West (left) handles
          newLeft = initialZone.left + deltaX;
          newWidth = initialZone.width - deltaX;
          if (newLeft < 0) {
            newWidth += newLeft;
            newLeft = 0;
          }
          if (newWidth < 5) {
            newLeft = initialZone.left + initialZone.width - 5;
            newWidth = 5;
          }
        }
        if (resizeHandle.includes('e')) {
          // East (right) handles
          newWidth = initialZone.width + deltaX;
          if (newLeft + newWidth > 100) {
            newWidth = 100 - newLeft;
          }
          if (newWidth < 5) newWidth = 5;
        }
        if (resizeHandle.includes('n')) {
          // North (top) handles
          newTop = initialZone.top + deltaY;
          newHeight = initialZone.height - deltaY;
          if (newTop < 0) {
            newHeight += newTop;
            newTop = 0;
          }
          if (newHeight < 5) {
            newTop = initialZone.top + initialZone.height - 5;
            newHeight = 5;
          }
        }
        if (resizeHandle.includes('s')) {
          // South (bottom) handles
          newHeight = initialZone.height + deltaY;
          if (newTop + newHeight > 100) {
            newHeight = 100 - newTop;
          }
          if (newHeight < 5) newHeight = 5;
        }
        
        setZones(prev => {
          const updated = [...prev];
          updated[selectedZoneIndex] = {
            ...updated[selectedZoneIndex],
            left: Math.round(newLeft * 10) / 10,
            top: Math.round(newTop * 10) / 10,
            width: Math.round(newWidth * 10) / 10,
            height: Math.round(newHeight * 10) / 10
          };
          return updated;
        });
      }
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
      setInitialZone(null);
    };
    
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, initialZone, selectedZoneIndex, resizeHandle]);


  const generateZoneId = () => {
    return `zone_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const addZone = () => {
    // Find the highest zIndex and add 1
    const maxZIndex = zones.length > 0 ? Math.max(...zones.map(z => z.zIndex || 0)) : -1;
    
    const newZone = {
      id: generateZoneId(),
      name: `Zone ${zones.length + 1}`,
      top: 0,
      left: 0,
      width: 50,
      height: 50,
      contentType: 'playlist',
      contentId: '',
      color: ZONE_COLORS[zones.length % ZONE_COLORS.length],
      fitMode: 'fit',
      zIndex: maxZIndex + 1
    };
    setZones([...zones, newZone]);
    setSelectedZoneIndex(zones.length);
  };

  const updateZone = (index, field, value) => {
    setZones(prevZones => {
      const updatedZones = [...prevZones];
      updatedZones[index] = {
        ...updatedZones[index],
        [field]: value
      };
      return updatedZones;
    });
  };

  const updateZoneType = (index, newType) => {
    setZones(prevZones => {
      const updatedZones = [...prevZones];
      updatedZones[index] = {
        ...updatedZones[index],
        contentType: newType,
        contentId: ''
      };
      return updatedZones;
    });
  };

  const deleteZone = (index) => {
    const updatedZones = zones.filter((_, i) => i !== index);
    setZones(updatedZones);
    setSelectedZoneIndex(null);
  };

  // Z-index controls - modify zIndex property, not array order
  const bringToFront = () => {
    if (selectedZoneIndex === null) return;
    const maxZIndex = Math.max(...zones.map(z => z.zIndex || 0));
    setZones(prev => {
      const updated = [...prev];
      updated[selectedZoneIndex] = {
        ...updated[selectedZoneIndex],
        zIndex: maxZIndex + 1
      };
      return updated;
    });
  };

  const bringForward = () => {
    if (selectedZoneIndex === null) return;
    const currentZIndex = zones[selectedZoneIndex].zIndex || 0;
    // Find zones with higher zIndex and get the lowest one
    const higherZones = zones.filter(z => (z.zIndex || 0) > currentZIndex);
    if (higherZones.length === 0) return;
    const nextZIndex = Math.min(...higherZones.map(z => z.zIndex || 0));
    
    setZones(prev => {
      const updated = [...prev];
      // Swap zIndex with the zone that has nextZIndex
      updated.forEach((zone, i) => {
        if ((zone.zIndex || 0) === nextZIndex) {
          updated[i] = { ...zone, zIndex: currentZIndex };
        }
      });
      updated[selectedZoneIndex] = {
        ...updated[selectedZoneIndex],
        zIndex: nextZIndex
      };
      return updated;
    });
  };

  const sendBackward = () => {
    if (selectedZoneIndex === null) return;
    const currentZIndex = zones[selectedZoneIndex].zIndex || 0;
    // Find zones with lower zIndex and get the highest one
    const lowerZones = zones.filter(z => (z.zIndex || 0) < currentZIndex);
    if (lowerZones.length === 0) return;
    const prevZIndex = Math.max(...lowerZones.map(z => z.zIndex || 0));
    
    setZones(prev => {
      const updated = [...prev];
      // Swap zIndex with the zone that has prevZIndex
      updated.forEach((zone, i) => {
        if ((zone.zIndex || 0) === prevZIndex) {
          updated[i] = { ...zone, zIndex: currentZIndex };
        }
      });
      updated[selectedZoneIndex] = {
        ...updated[selectedZoneIndex],
        zIndex: prevZIndex
      };
      return updated;
    });
  };

  const sendToBack = () => {
    if (selectedZoneIndex === null) return;
    const minZIndex = Math.min(...zones.map(z => z.zIndex || 0));
    setZones(prev => {
      const updated = [...prev];
      updated[selectedZoneIndex] = {
        ...updated[selectedZoneIndex],
        zIndex: minZIndex - 1
      };
      return updated;
    });
  };

  // Overlay upload handler
  const handleOverlayUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (PNG recommended for transparency)');
      return;
    }

    setUploadingOverlay(true);

    try {
      const timestamp = Date.now();
      const fileName = `overlay_${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;

      const functions = getFunctions();
      const getUploadUrl = httpsCallable(functions, 'getUploadUrl');
      const result = await getUploadUrl({ fileName, contentType: file.type });

      await fetch(result.data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });

      const fileUrl = result.data.fileUrl;
      setOverlayUrl(fileUrl);
    } catch (error) {
      console.error('Overlay upload failed:', error);
      alert('Failed to upload overlay: ' + error.message);
    } finally {
      setUploadingOverlay(false);
    }
  };

  const handleSave = async () => {
    if (!layoutName.trim()) {
      alert('Please enter a layout name');
      return;
    }

    if (zones.length === 0) {
      alert('Please add at least one zone');
      return;
    }

    if (!layoutContentType) {
      alert('Please choose a content type (Interval or Exercise) for this layout');
      return;
    }

    setSaving(true);

    try {
      const layoutData = {
        name: layoutName,
        type: 'layout',
        layoutContentType,
        isTemplate,
        orientation,
        overlayUrl: overlayUrl || null,
        zones: zones.map(zone => ({
          id: zone.id,
          name: zone.name,
          top: parseFloat(zone.top) || 0,
          left: parseFloat(zone.left) || 0,
          width: parseFloat(zone.width) || 50,
          height: parseFloat(zone.height) || 50,
          contentType: zone.contentType,
          contentId: zone.contentId,
          color: zone.color,
          fitMode: zone.fitMode || 'fit',
          zIndex: zone.zIndex || 0
        })),
        updatedAt: Date.now()
      };

      if (layoutId) {
        await updateDoc(doc(db, 'assets', layoutId), layoutData);
      } else {
        layoutData.createdAt = Date.now();
        layoutData.uploadedAt = Date.now();
        await addDoc(collection(db, 'assets'), layoutData);
      }

      navigate('/assets');
    } catch (error) {
      console.error('Error saving layout:', error);
      alert('Error saving layout. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // List drag handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newZones = [...zones];
    const [draggedZone] = newZones.splice(draggedIndex, 1);
    newZones.splice(dropIndex, 0, draggedZone);
    setZones(newZones);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Canvas drag handlers
  const handleCanvasZoneMouseDown = (e, index) => {
    e.stopPropagation();
    
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mouseXPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const mouseYPercent = ((e.clientY - rect.top) / rect.height) * 100;
    
    setSelectedZoneIndex(index);
    setDragStart({ x: mouseXPercent, y: mouseYPercent });
    setInitialZone({ ...zones[index] });
    setIsDragging(true);
    
    // Scroll within the zone list container only, not the page
    setTimeout(() => {
      const zoneElement = document.getElementById(`zone-settings-${index}`);
      const zoneListContainer = document.getElementById('zone-list-container');
      if (zoneElement && zoneListContainer) {
        const containerRect = zoneListContainer.getBoundingClientRect();
        const elementRect = zoneElement.getBoundingClientRect();
        const scrollTop = zoneListContainer.scrollTop + (elementRect.top - containerRect.top) - 10;
        zoneListContainer.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    }, 50);
  };

  const handleResizeMouseDown = (e, handle) => {
    e.stopPropagation();
    
    if (!canvasRef.current || selectedZoneIndex === null) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mouseXPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const mouseYPercent = ((e.clientY - rect.top) / rect.height) * 100;
    
    setDragStart({ x: mouseXPercent, y: mouseYPercent });
    setInitialZone({ ...zones[selectedZoneIndex] });
    setResizeHandle(handle);
    setIsResizing(true);
  };

  // Asset picker functions
  const openAssetPicker = (zoneIndex) => {
    setAssetPickerZoneIndex(zoneIndex);
    setAssetSearchQuery('');
    setShowAssetPicker(true);
  };

  const selectAsset = (asset) => {
    if (assetPickerZoneIndex !== null) {
      updateZone(assetPickerZoneIndex, 'contentId', asset.id);
    }
    setShowAssetPicker(false);
    setAssetPickerZoneIndex(null);
  };

  const selectPlaylist = (playlist) => {
    if (assetPickerZoneIndex !== null) {
      updateZone(assetPickerZoneIndex, 'contentId', playlist.id);
    }
    setShowAssetPicker(false);
    setAssetPickerZoneIndex(null);
  };

  const getContentName = (zone) => {
    if (!zone.contentId) return 'Not assigned';
    
    if (zone.contentType === 'playlist') {
      const playlist = playlists.find(p => p.id === zone.contentId);
      return playlist ? playlist.name : 'Unknown playlist';
    } else {
      const asset = assets.find(a => a.id === zone.contentId);
      return asset ? asset.name : 'Unknown asset';
    }
  };

  const filteredPickerAssets = assets.filter(asset => {
    const matchesSearch = asset.name.toLowerCase().includes(assetSearchQuery.toLowerCase());
    // Asset zones only ever hold images — never offer videos here.
    return matchesSearch && asset.type === 'image';
  });

  const filteredPickerPlaylists = playlists.filter(playlist => {
    const matchesSearch = playlist.name.toLowerCase().includes(assetSearchQuery.toLowerCase());
    const matchesOwner = isAdmin
      ? !playlist.userId || playlist.userId === currentUser?.uid
      : playlist.userId === currentUser?.uid;
    // Gate (doorway 3): only playlists whose type matches this layout's content type.
    // Untyped legacy playlists (no playlistContentType) are hidden until typed.
    const matchesType = !!layoutContentType && playlist.playlistContentType === layoutContentType;
    return matchesSearch && matchesOwner && matchesType;
  });

 

  // Resize handles component
  const ResizeHandles = ({ zone }) => {
    const handleSize = 8;
    const handles = [
      { position: 'nw', cursor: 'nw-resize', top: -handleSize/2, left: -handleSize/2 },
      { position: 'n', cursor: 'n-resize', top: -handleSize/2, left: '50%', transform: 'translateX(-50%)' },
      { position: 'ne', cursor: 'ne-resize', top: -handleSize/2, right: -handleSize/2 },
      { position: 'e', cursor: 'e-resize', top: '50%', right: -handleSize/2, transform: 'translateY(-50%)' },
      { position: 'se', cursor: 'se-resize', bottom: -handleSize/2, right: -handleSize/2 },
      { position: 's', cursor: 's-resize', bottom: -handleSize/2, left: '50%', transform: 'translateX(-50%)' },
      { position: 'sw', cursor: 'sw-resize', bottom: -handleSize/2, left: -handleSize/2 },
      { position: 'w', cursor: 'w-resize', top: '50%', left: -handleSize/2, transform: 'translateY(-50%)' },
    ];

    return handles.map(handle => (
      <div
        key={handle.position}
        onMouseDown={(e) => handleResizeMouseDown(e, handle.position)}
        style={{
          position: 'absolute',
          width: handleSize,
          height: handleSize,
          backgroundColor: '#1a73e8',
          border: '1px solid white',
          cursor: handle.cursor,
          top: handle.top,
          left: handle.left,
          right: handle.right,
          bottom: handle.bottom,
          transform: handle.transform,
          zIndex: 10
        }}
      />
    ));
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div style={{ 
  height: 'calc(100vh - 100px)', 
  overflow: 'auto', 
  display: 'flex', 
  flexDirection: 'column',
  minWidth: '1000px'
}}>
      {/* Header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/assets')}
            style={{ padding: '8px 12px' }}
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="page-title">
            {layoutId ? 'Edit Layout' : 'Create Layout'}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            onClick={() => setIsTemplate(v => !v)}
            title="Offer this layout as a preset in the template picker"
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              border: isTemplate ? '1px solid #e8590c' : '1px solid #d0d0d0',
              background: isTemplate ? '#fff3ea' : '#fff',
              borderRadius: '8px', padding: '7px 12px', cursor: 'pointer', userSelect: 'none'
            }}
          >
            <span style={{
              width: '16px', height: '16px', borderRadius: '4px',
              border: isTemplate ? 'none' : '1.5px solid #b0b0b0',
              background: isTemplate ? '#e8590c' : 'transparent',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
            }}>
              {isTemplate && <Check size={12} color="#fff" strokeWidth={3} />}
            </span>
            <span style={{ fontSize: '13px', fontWeight: 500, color: isTemplate ? '#b8460a' : '#555' }}>Template</span>
          </div>
          <button 
            className="btn btn-primary" 
            onClick={handleSave}
            disabled={saving}
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Layout'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px', flex: 1, overflow: 'hidden' }}>
        {/* Left Panel - Zone List & Properties */}
        <div style={{ width: '380px', flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div className="card" style={{ marginBottom: '16px', flexShrink: 0 }}>
            <div className="form-group">
              <label className="form-label">Layout Name *</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Core Layout, Gym Display"
                value={layoutName}
                onChange={(e) => setLayoutName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Content Type *</label>
              <select
                className="form-input"
                value={layoutContentType}
                onChange={(e) => setLayoutContentType(e.target.value)}
              >
                <option value="">Select content type…</option>
                <option value="interval">Interval</option>
                <option value="exercise">Exercise</option>
              </select>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                Gates which content can be assigned to this layout's zones.
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Orientation</label>
              <select
                className="form-input"
                value={orientation}
                onChange={(e) => setOrientation(e.target.value)}
              >
                <option value="landscape">Landscape</option>
                <option value="portrait">Portrait</option>
              </select>
            </div>

            {/* Overlay Upload - Compact */}
<div className="form-group" style={{ marginBottom: 0 }}>
  <label className="form-label">Overlay Image</label>
  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
    <label 
      className="btn btn-secondary"
      style={{ 
        cursor: uploadingOverlay ? 'not-allowed' : 'pointer',
        opacity: uploadingOverlay ? 0.6 : 1,
        padding: '6px 12px',
        fontSize: '13px'
      }}
    >
      {uploadingOverlay ? 'Uploading...' : overlayUrl ? 'Change' : 'Upload'}
      <input
        type="file"
        accept="image/*"
        onChange={handleOverlayUpload}
        disabled={uploadingOverlay}
        style={{ display: 'none' }}
      />
    </label>
    {overlayUrl && (
      <>
        <img 
          src={overlayUrl} 
          alt="Overlay" 
          style={{ height: '32px', borderRadius: '4px', background: '#333' }} 
        />
        <button
          className="btn btn-secondary"
          onClick={() => setOverlayUrl('')}
          style={{ padding: '4px 8px' }}
          title="Remove"
        >
          <X size={14} />
        </button>
      </>
    )}
    {!overlayUrl && <span style={{ fontSize: '12px', color: '#999' }}>PNG recommended</span>}
  </div>
</div>
          </div>

          {/* Add Zone Button */}
          <button
            className="btn btn-primary"
            onClick={addZone}
            style={{ width: '100%', marginBottom: '16px', flexShrink: 0 }}
          >
            <Plus size={18} />
            Add Zone
          </button>

          {/* Zone List - This is the only part that scrolls */}
          <div id="zone-list-container" className="card" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {zones.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No zones yet. Click "Add Zone" to start.
              </div>
            ) : (
              zones.map((zone, index) => (
                <div
                  key={zone.id}
                  id={`zone-settings-${index}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onClick={() => setSelectedZoneIndex(index)}
                  style={{
                    padding: '16px',
                    marginBottom: '12px',
                    borderRadius: '8px',
                    border: selectedZoneIndex === index ? '2px solid #d32f2f' : '1px solid #eee',
                    background: selectedZoneIndex === index ? '#fff5f5' : '#fff',
                    cursor: 'pointer',
                    borderTop: dragOverIndex === index && draggedIndex > index ? '3px solid #d32f2f' : undefined,
                    borderBottom: dragOverIndex === index && draggedIndex < index ? '3px solid #d32f2f' : undefined,
                    opacity: draggedIndex === index ? 0.5 : 1
                  }}
                >
                  {/* Zone Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <GripVertical size={16} color="#999" style={{ cursor: 'grab' }} />
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                        backgroundColor: zone.color,
                        flexShrink: 0
                      }}
                    />
                    <input
                      type="text"
                      className="form-input"
                      value={zone.name}
                      onChange={(e) => updateZone(index, 'name', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ padding: '4px 8px', fontSize: '14px', fontWeight: 500, flex: 1 }}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteZone(index);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#999',
                        padding: '4px'
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {/* Position & Size */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                    <div>
                      <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '2px' }}>Top(%)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={zone.top}
                        onChange={(e) => updateZone(index, 'top', e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ padding: '4px 6px', fontSize: '13px' }}
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '2px' }}>Left(%)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={zone.left}
                        onChange={(e) => updateZone(index, 'left', e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ padding: '4px 6px', fontSize: '13px' }}
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '2px' }}>Width(%)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={zone.width}
                        onChange={(e) => updateZone(index, 'width', e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ padding: '4px 6px', fontSize: '13px' }}
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '2px' }}>Height(%)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={zone.height}
                        onChange={(e) => updateZone(index, 'height', e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ padding: '4px 6px', fontSize: '13px' }}
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    </div>
                  </div>

                  {/* Content Type */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>Type</label>
                    <select
                      className="form-input"
                      value={zone.contentType}
                      onChange={(e) => {
                        e.stopPropagation();
                        updateZoneType(index, e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{ 
                        padding: '8px 12px', 
                        fontSize: '14px',
                        width: '100%',
                        cursor: 'pointer',
                        appearance: 'menulist'
                      }}
                    >
                      <option value="playlist">Playlist</option>
                      <option value="asset">Asset</option>
                    </select>
                  </div>

                  {/* Fit Mode - only show for Asset type (images) */}
                  {zone.contentType === 'asset' && (
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>Fit Screen</label>
                      <select
                        className="form-input"
                        value={zone.fitMode || 'fit'}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateZone(index, 'fitMode', e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{ 
                          padding: '8px 12px', 
                          fontSize: '14px',
                          width: '100%',
                          cursor: 'pointer',
                          appearance: 'menulist'
                        }}
                      >
                        <option value="none">None</option>
                        <option value="stretch">Stretch</option>
                        <option value="fit">Fit</option>
                        <option value="zoom">Zoom</option>
                      </select>
                    </div>
                  )}

                  {/* Content Selection with Change Button */}
                  <div>
                    <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>Content</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div 
                        style={{ 
                          flex: 1, 
                          padding: '8px 12px', 
                          background: '#f5f5f5', 
                          borderRadius: '6px',
                          fontSize: '13px',
                          color: zone.contentId ? '#333' : '#999',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {getContentName(zone)}
                      </div>
                      {zone.contentId && (
                        <button
                          className="btn btn-secondary"
                          title="Clear content"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateZone(index, 'contentId', '');
                          }}
                          style={{ padding: '6px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        >
                          <X size={16} />
                        </button>
                      )}
                      <button
                        className="btn btn-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          openAssetPicker(index);
                        }}
                        style={{ padding: '6px 12px', fontSize: '13px', whiteSpace: 'nowrap' }}
                      >
                        Change
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel - Visual Preview */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '12px',
              paddingBottom: '12px',
              borderBottom: '1px solid #eee',
              flexShrink: 0
            }}>
              <h3 style={{ margin: 0 }}>Preview</h3>
              <span style={{ color: '#999', fontSize: '14px' }}>1920 × 1080</span>
            </div>

            {/* Z-Index Toolbar */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
              padding: '8px 12px',
              background: '#f5f5f5',
              borderRadius: '8px',
              flexShrink: 0
            }}>
              <span style={{ fontSize: '13px', color: '#666', marginRight: '8px' }}>Layer:</span>
              <button
                onClick={sendToBack}
                disabled={selectedZoneIndex === null}
                title="Send to Back"
                style={{
                  padding: '6px 8px',
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: selectedZoneIndex === null ? 'not-allowed' : 'pointer',
                  opacity: selectedZoneIndex === null ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronsDown size={16} />
              </button>
              <button
                onClick={sendBackward}
                disabled={selectedZoneIndex === null}
                title="Send Backward"
                style={{
                  padding: '6px 8px',
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: selectedZoneIndex === null ? 'not-allowed' : 'pointer',
                  opacity: selectedZoneIndex === null ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronDown size={16} />
              </button>
              <button
                onClick={bringForward}
                disabled={selectedZoneIndex === null}
                title="Bring Forward"
                style={{
                  padding: '6px 8px',
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: selectedZoneIndex === null ? 'not-allowed' : 'pointer',
                  opacity: selectedZoneIndex === null ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronUp size={16} />
              </button>
              <button
                onClick={bringToFront}
                disabled={selectedZoneIndex === null}
                title="Bring to Front"
                style={{
                  padding: '6px 8px',
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: selectedZoneIndex === null ? 'not-allowed' : 'pointer',
                  opacity: selectedZoneIndex === null ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronsUp size={16} />
              </button>
              
              {selectedZoneIndex !== null && (
                <button
                  onClick={() => deleteZone(selectedZoneIndex)}
                  title="Delete Zone"
                  style={{
                    padding: '6px 8px',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: 'auto',
                    color: '#d32f2f'
                  }}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>

            {/* Canvas Preview Container */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div
                ref={canvasRef}
                style={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: orientation === 'landscape' ? '56.25%' : '120%',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  cursor: isDragging ? 'grabbing' : 'default',
                  flexShrink: 0
                }}
              >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0
                }}
              >
              {/* Render zones sorted by zIndex */}
              {[...zones]
                .map((zone, originalIndex) => ({ zone, originalIndex }))
                .sort((a, b) => (a.zone.zIndex || 0) - (b.zone.zIndex || 0))
                .map(({ zone, originalIndex }) => (
                  <div
                    key={zone.id}
                    onMouseDown={(e) => handleCanvasZoneMouseDown(e, originalIndex)}
                    style={{
                      position: 'absolute',
                      top: `${zone.top}%`,
                      left: `${zone.left}%`,
                      width: `${zone.width}%`,
                      height: `${zone.height}%`,
                      backgroundColor: zone.color,
                      border: selectedZoneIndex === originalIndex ? '2px solid #1a73e8' : '2px solid rgba(0,0,0,0.2)',
                      borderRadius: '4px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isDragging && selectedZoneIndex === originalIndex ? 'grabbing' : 'grab',
                      boxSizing: 'border-box',
                      userSelect: 'none',
                      zIndex: zone.zIndex || 0
                    }}
                  >
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: 600, 
                      color: 'rgba(0,0,0,0.7)',
                      textAlign: 'center',
                      padding: '4px',
                      pointerEvents: 'none'
                    }}>
                      {zone.name}
                    </div>
                    <div style={{ 
                      fontSize: '11px', 
                      color: 'rgba(0,0,0,0.5)',
                      textAlign: 'center',
                      padding: '0 4px',
                      pointerEvents: 'none'
                    }}>
                      {getContentName(zone)}
                    </div>
                    
                    {/* Resize Handles - only show for selected zone */}
                    {selectedZoneIndex === originalIndex && <ResizeHandles zone={zone} />}
                  </div>
                ))}

                {/* Overlay Preview */}
                {overlayUrl && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      pointerEvents: 'none',
                      zIndex: 1000
                    }}
                  >
                    <img
                      src={overlayUrl}
                      alt="Overlay"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'fill'
                      }}
                    />
                  </div>
                )}

                {zones.length === 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: '#999',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '18px', marginBottom: '8px' }}>No zones defined</div>
                    <div style={{ fontSize: '14px' }}>Click "Add Zone" to get started</div>
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Asset Picker Modal */}
      {showAssetPicker && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowAssetPicker(false)}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '800px',
              maxWidth: '90vw',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ 
              padding: '20px', 
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px' }}>
                Select {assetPickerZoneIndex !== null && zones[assetPickerZoneIndex]?.contentType === 'playlist' ? 'Playlist' : 'Asset'}
              </h2>
              <button
                onClick={() => setShowAssetPicker(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Search and Filters */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <Search 
                    size={18} 
                    style={{ 
                      position: 'absolute', 
                      left: '12px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: '#999'
                    }} 
                  />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="form-input"
                    value={assetSearchQuery}
                    onChange={(e) => setAssetSearchQuery(e.target.value)}
                    style={{ paddingLeft: '40px', width: '100%' }}
                  />
                </div>
                {assetPickerZoneIndex !== null && zones[assetPickerZoneIndex]?.contentType === 'asset' && (
                  <div style={{ fontSize: '13px', color: '#888' }}>
                    Images only
                  </div>
                )}
              </div>
            </div>

            {/* Content Grid */}
            <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
              {assetPickerZoneIndex !== null && zones[assetPickerZoneIndex]?.contentType === 'playlist' ? (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                  gap: '16px' 
                }}>
                  {filteredPickerPlaylists.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: '#999' }}>
                      {!layoutContentType
                        ? 'Choose a content type for this layout first'
                        : `No ${layoutContentType} playlists found`}
                    </div>
                  ) : (
                    filteredPickerPlaylists.map(playlist => (
                      <div
                        key={playlist.id}
                        onClick={() => selectPlaylist(playlist)}
                        style={{
                          padding: '16px',
                          border: '2px solid #eee',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d32f2f'}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = '#eee'}
                      >
                        <div style={{ 
                          width: '100%', 
                          height: '80px', 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '12px'
                        }}>
                          <Film size={32} color="white" />
                        </div>
                        <div style={{ fontWeight: 500, fontSize: '14px' }}>{playlist.name}</div>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                          {playlist.assets?.length || 0} items
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
                  gap: '16px' 
                }}>
                  {filteredPickerAssets.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: '#999' }}>
                      No images found
                    </div>
                  ) : (
                    filteredPickerAssets.map(asset => (
                      <AssetPickerCard 
                        key={asset.id} 
                        asset={asset} 
                        onSelect={selectAsset}
                        signedUrlMap={signedUrlMap}
                      />
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ 
              padding: '16px 20px', 
              borderTop: '1px solid #eee',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowAssetPicker(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Asset picker card with sprite hover scrub
function AssetPickerCard({ asset, onSelect,signedUrlMap}) {
  const [isHovering, setIsHovering] = useState(false);
  const [spriteFrame, setSpriteFrame] = useState(0);
  const scrubIntervalRef = useRef(null);

  const handleMouseEnter = () => {
    setIsHovering(true);
    
    if (asset.type !== 'video' || !asset.spriteUrl) return;
    
    setSpriteFrame(0);
    scrubIntervalRef.current = setInterval(() => {
      setSpriteFrame(prev => (prev + 1) % 4);
    }, 600);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setSpriteFrame(0);
    
    if (scrubIntervalRef.current) {
      clearInterval(scrubIntervalRef.current);
      scrubIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (scrubIntervalRef.current) {
        clearInterval(scrubIntervalRef.current);
      }
    };
  }, []);

  return (
    <div
      onClick={() => onSelect(asset)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        border: '2px solid #eee',
        borderRadius: '8px',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'all 0.15s ease'
      }}
      onMouseOver={(e) => e.currentTarget.style.borderColor = '#d32f2f'}
      onMouseOut={(e) => e.currentTarget.style.borderColor = '#eee'}
    >
      <div style={{ 
        width: '100%', 
        height: '100px', 
        background: '#1a1a1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {asset.type === 'video' ? (
          <>
            {asset.spriteUrl && isHovering ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${asset.spriteUrl})`,
                  backgroundPosition: `${(spriteFrame / 3) * 100}% 0`,
                  backgroundSize: '400% 100%',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            ) : (
              <>
                {asset.spriteUrl ? (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url(${asset.spriteUrl})`,
                      backgroundPosition: '0% 0',
                      backgroundSize: '400% 100%',
                      backgroundRepeat: 'no-repeat'
                    }}
                  />
                ) : asset.thumbnailUrl ? (
                  <img 
                    src={asset.thumbnailUrl} 
                    alt={asset.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Film size={32} color="#999" />
                )}
              </>
            )}
          </>
        ) : asset.type === 'image' && asset.url ? (
          <img 
            src={signedUrlMap?.[asset.key] || asset.url} 
            alt={asset.name}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Image size={32} color="#999" />
        )}
      </div>
      <div style={{ padding: '10px' }}>
        <div style={{ 
          fontWeight: 500, 
          fontSize: '13px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {asset.name}
        </div>
        <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
          {asset.type === 'video' ? 'Video' : 'Image'}
        </div>
      </div>
    </div>
  );
}

export default LayoutEditor;